import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { otpInputVariants } from './otp-input.variants';

/**
 * One-time code entry drawn as single-digit boxes (`3 – 3` for six):
 *
 * ```html
 * <vx-otp-input formControlName="code" ariaLabel="Code" (completed)="submit()" />
 * ```
 *
 * One real `<input>` (numeric, `autocomplete="one-time-code"`) lies over
 * the boxes and carries focus, typing, paste and the SMS / mail autofill;
 * the boxes only mirror its value and the caret position. Implements
 * `ControlValueAccessor` (string of digits). `completed` fires once all
 * digits are in, so a page can submit without a button.
 */
@Component({
  selector: 'vx-otp-input',
  templateUrl: './otp-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // real box, never display:contents — focus rings and layout measure it
  host: { class: 'inline-flex font-brand' },
})
export class VxOtpInputComponent implements ControlValueAccessor {
  /** Number of digits; the boxes split into two even groups. */
  readonly length = input(6);

  /** Accessible name of the input. Already translated. */
  readonly ariaLabel = input.required<string>();

  /** Marks the boxes as wrong (e.g. a rejected code). */
  readonly invalid = input(false);

  readonly disabled = input(false);

  /**
   * The code is being checked: input pauses and a brightness wave runs
   * across the digits (the design's "Verifying…" state).
   */
  readonly verifying = input(false);

  /** All digits are entered; carries the code. */
  readonly completed = output<string>();

  private readonly ngControl = inject(NgControl, {
    self: true,
    optional: true,
  });

  private readonly field =
    viewChild.required<ElementRef<HTMLInputElement>>('field');

  private readonly disabledByForm = signal(false);

  private onChange: (value: string) => void = () => undefined;

  private onTouched: () => void = () => undefined;

  protected readonly value = signal('');

  protected readonly focused = signal(false);

  protected readonly isDisabled = computed(
    () => this.disabled() || this.disabledByForm() || this.verifying()
  );

  /** Stagger of the verifying wave from one box to the next. */
  protected readonly waveStepMs = 120;

  /** Box indexes per group, e.g. [[0,1,2],[3,4,5]]. */
  protected readonly groups = computed(() => {
    const length = this.length();
    const half = Math.ceil(length / 2);
    const indexes = Array.from({ length }, (_, i) => i);
    return [indexes.slice(0, half), indexes.slice(half)];
  });

  /** The box the next digit goes into; the last one once full. */
  protected readonly activeIndex = computed(() =>
    Math.min(this.value().length, this.length() - 1)
  );

  // tv() slots are functions — resolve them to strings before binding
  protected readonly classes = computed(() => {
    const state = {
      invalid: this.invalid(),
      // verifying pauses input without the disabled look
      disabled: this.disabled() || this.disabledByForm(),
      verifying: this.verifying(),
    };
    const slots = otpInputVariants(state);
    return {
      root: slots.root(),
      group: slots.group(),
      field: slots.field(),
      box: slots.box(),
      digit: slots.digit(),
      boxActive: otpInputVariants({ ...state, active: true }).box(),
    };
  });

  constructor() {
    this.initValueAccessor();
  }

  /**
   * Self-registration instead of an `NG_VALUE_ACCESSOR` provider — the
   * provider form would create a circular dependency on the component itself.
   */
  private initValueAccessor(): void {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  /** Focuses the input, e.g. after a wrong code was cleared. */
  focus(): void {
    this.field().nativeElement.focus();
  }

  protected handleInput(event: Event): void {
    const element = event.target as HTMLInputElement;
    // paste and autofill can bring spaces, dashes or more than fits
    const digits = element.value.replace(/\D/g, '').slice(0, this.length());
    element.value = digits;
    this.value.set(digits);
    this.onChange(digits);
    if (digits.length === this.length()) {
      this.completed.emit(digits);
    }
  }

  protected handleFocus(): void {
    this.focused.set(true);
    // keep the caret at the end, where the next digit goes
    const element = this.field().nativeElement;
    element.setSelectionRange(element.value.length, element.value.length);
  }

  protected handleBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  writeValue(value: string | null | undefined): void {
    const digits = (value ?? '').replace(/\D/g, '').slice(0, this.length());
    this.value.set(digits);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm.set(isDisabled);
  }
}
